/* eslint-disable react-hooks/exhaustive-deps */
//#region Libraries
import { useState, useEffect } from "react";
//#endregion
//#region Services
import { requestGetResources } from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import { getDatePartsByMillis, MONTHS } from "components/CustomDatePicker/utils/helpers";
import { TIMEOUT_HIDDEN_TOAST_MESSAGE } from "components/ToastMessage/ToastMessage";
import { ERROR_GET_SERVICES, INIT_FILTER_FIELDS } from "../constants";
import { QUANTITY_PER_PAGE } from "views/components/Schedules/utils/constants";
import { wait } from "utils/others";
//#endregion

const useServiceList = ({
    filters,
    notifications: {
        loadingRequest,
        setToastMessage
    },
    paginator, isLibrarian
}) => {
    //#region States
    const [serviceList, setServiceList] = useState(null);
    const [showDialogNonBusinessDay, setShowDialogNonBusinessDay] = useState(false);
    const [nonBusinessDay, setNonBusinessDay] = useState("");
    const [triggerRequest, setTriggerRequest] = useState(false);
    const [searchedFilters, setSearchedFilters] = useState({ 
        ...INIT_FILTER_FIELDS,
        page: 0
    });
    //#endregion
    //#region Effects
    useEffect(() => {
        if (!isLibrarian) return;
        setSearchedFilters({ 
            ...searchedFilters, 
            campusId: filters.value.campusId 
        });
    }, [filters.value.campusId]);
    useEffect(() => {
        if (!triggerRequest) return;
        setTriggerRequest(false);
        fillServiceList();
    }, [triggerRequest]);
    //#endregion
    //#region Functions
    const toggleShowDialogNonBusinessDay = (value = null) => {
        setShowDialogNonBusinessDay(prev => value === null ? !prev : value);
    }
    const filtersControlsHaveBeenChanged = () => {
        const { libraryId, date, resourceTypeId } = filters.value;
        return Boolean(
            libraryId !== searchedFilters.libraryId ||
            date !== searchedFilters.date ||
            resourceTypeId !== searchedFilters.resourceTypeId
        );
    }
    const fillServiceList = async (params) => {
        const { messageForToast, noFill } = params || { messageForToast: null, noFill: false };
        if (noFill) {
            loadingRequest.hide();
            showToastAfterFilledServiceList(messageForToast);
            return;
        }
        loadingRequest.show();
        // Realizando petición
        const newPage = filtersControlsHaveBeenChanged() ? 1 : paginator.data.currentPage;
        const fetchData = await requestGetResources({
            ...filters.value,
            page: newPage
        });
        loadingRequest.hide();
        if (!manageError(fetchData.error)) 
            return;
        setSearchedFilters({
            date: filters.value.date, 
            campusId: filters.value.campusId,
            libraryId: filters.value.libraryId, 
            resourceTypeId: filters.value.resourceTypeId, 
            page: newPage 
        });
        // Setteando cantidad de páginas
        paginator.movePage(newPage);
        paginator.setQuantityPages({
            quantityPages: Math.ceil(fetchData.total/QUANTITY_PER_PAGE),
            totalRecords: fetchData.total
        });
        // Setteando lista de servicios
        setServiceList(fetchData.resources);
        showToastAfterFilledServiceList(messageForToast);
    }
    const clearServiceList = () => {
        setServiceList(null);
    }
    const showToastAfterFilledServiceList = async message => {
        if (!message) return;
        // Configurando toast de carga exitosa
        const successToast = {
            hidden: false,
            message
        };
        // Programando toast message
        setToastMessage({ ...successToast });
        await wait(TIMEOUT_HIDDEN_TOAST_MESSAGE);
        successToast.hidden = true;
        setToastMessage({ ...successToast });
    }
    const manageError = error => {
        if (error !== ERROR_GET_SERVICES.NON_BUSINESS_DAY) 
            return true;
        const [year, month, date] = getDatePartsByMillis(filters.value.date);
        clearServiceList();
        filters.clear();
        paginator.clear();
        setNonBusinessDay(`${date} ${MONTHS[month].substring(0, 3)} ${year}`);
        setShowDialogNonBusinessDay(true);
        return false;
    }
    const toggleStudentToGroupRoomReservation = ({
        resourceId, 
        reservationId,
        addAction,
        deleteAction
    }) => {
        let getOthers = () => [];
        if (addAction) {
            const {
                newStudents
            } = addAction;
            getOthers = others => [...others, ...newStudents];
        }
        if (deleteAction) {
            const {
                studentReservationDetailIds
            } = deleteAction;
            getOthers = others => others.filter(({ reservationDetailId }) => studentReservationDetailIds.includes(reservationDetailId));
        }
        const newServiceList = serviceList.map(resourceItem => {
            return resourceItem.resource.id === resourceId 
                ? {
                    ...resourceItem,
                    reservations: resourceItem.reservations.map(reservation => {
                        return reservationId === reservation.id 
                        ? { 
                            ...reservation,
                            student: {
                                ...reservation.student,
                                others: getOthers(reservation.student.others)
                            }
                        } : { ...reservation };
                    })
                }
                : { ...resourceItem };
        });
        setServiceList(newServiceList);
    }
    //#endregion
    return {
        serviceList: {
            setTriggerRequest,
            triggerFilling: () => setTriggerRequest(true),
            fill: fillServiceList,
            clear: clearServiceList,
            value: serviceList,
            toggleStudentToGroupRoomReservation
        },
        searchedFilters,
        nonBusinessDay: {
            value: nonBusinessDay,
            dialog: {
                show: showDialogNonBusinessDay,
                toggle: toggleShowDialogNonBusinessDay
            }
        }
    };
}

export default useServiceList;